import { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import Icons from "../components/icons";
import HoverScrollbar from "../components/HoverScrollbar";

import { authFetch } from "../lib/api";

export default function HolidayList() {
  const [holidays, setHolidays] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newHoliday, setNewHoliday] = useState({
    name: "",
    date: "",
    type: "Public",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // holds holiday id to confirm delete
  const [editingHoliday, setEditingHoliday] = useState(null); // holds holiday object being edited
  const editingRowRef = useRef(null);
  const tableScrollRef = useRef(null);

  const year = 2026;

  const userRaw = localStorage.getItem("user");
  const userRole = userRaw ? JSON.parse(userRaw).role : null;
  // The holiday calendar is company-wide, so only admins may change it.
  // Managers (and everyone else) get a read-only view: no Add button and no
  // Action column. The backend enforces the same rule.
  const canManageHolidays = userRole === "admin";

  // ─── Fetch holidays from backend ───────────────────────────
  const fetchHolidays = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await authFetch(`/api/holidays?year=${year}`);
      const data = await res.json();
      if (data.success) {
        setHolidays(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError("Failed to load holidays.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Add holiday ────────────────────────────────────────────
  const handleAddHoliday = async () => {
    if (!newHoliday.name || !newHoliday.date) return;

    try {
      const res = await authFetch("/api/holidays", {
        method: "POST",
        body: JSON.stringify(newHoliday),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        alert(json?.message || "Failed to add holiday.");
        return;
      }
    } catch {
      alert("Network error — holiday not saved.");
      return;
    }

    setNewHoliday({ name: "", date: "", type: "Public" });
    setShowModal(false);
    fetchHolidays(); // refresh so the new holiday gets its real _id from the backend
  };

  // ─── Delete holiday ─────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      const res = await authFetch(`/api/holidays/${id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (data.success) {
        setDeleteConfirm(null);
        fetchHolidays(); // refresh
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Failed to delete holiday.");
    }
  };

  // ─── Save edit ──────────────────────────────────────────────
  const handleSaveEdit = async (overrideHoliday) => {
    const holidayToSave = overrideHoliday || editingHoliday;
    if (!holidayToSave) return;
    try {
      const res = await authFetch(`/api/holidays/${holidayToSave._id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: holidayToSave.name,
          date: holidayToSave.date,
          type: holidayToSave.type,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setEditingHoliday(null);
        fetchHolidays(); // refresh
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Failed to update holiday.");
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcoming = holidays.find((h) => new Date(h.date) >= today);

  useEffect(() => {
    fetchHolidays();
  }, []);

  // Clicking anywhere outside the active row exits edit mode without saving.
  useEffect(() => {
    if (!editingHoliday) return undefined;

    const closeEditOnOutsideClick = (event) => {
      if (editingRowRef.current && !editingRowRef.current.contains(event.target)) {
        setEditingHoliday(null);
      }
    };

    document.addEventListener("mousedown", closeEditOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeEditOnOutsideClick);
  }, [editingHoliday]);

  return (
    // No height or scroll of its own: the layout's content area already
    // scrolls, and only when the page genuinely does not fit. (A full-screen
    // h-screen wrapper here sat under the 70px top bar and always overflowed.)
    <div className="min-h-full font-[Poppins]">
      {/* MAIN CARD */}
      <div className="w-[95%] sm:w-[85%] mx-auto bg-white rounded-xl shadow-sm mt-6 sm:mt-10 mb-10 p-4 sm:p-6">
        {/* HEADER */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <div>
            <h2 className="text-[18px] font-semibold text-[#111827]">
              Holiday List
            </h2>
            <p className="text-[13px] text-[#6b7280]">
              View and manage company holidays
            </p>
          </div>
          <div className="flex gap-2">
            {/* Add button — admin only */}
            {canManageHolidays && (
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center justify-center bg-[#4253C7] text-white px-4 py-2 text-sm rounded-md w-[130px]"
              >
                Add Holiday
              </button>
            )}
          </div>
        </div>

        {loading && (
          <p className="text-sm text-gray-500 text-center py-4">
            Loading holidays...
          </p>
        )}

        {error && (
          <p className="text-sm text-red-500 text-center py-4">{error}</p>
        )}

        {/* UPCOMING */}
        {upcoming && (
          <div className="bg-[#f3f4f6] border border-[#d1d5db] rounded-md px-4 py-2 text-[13px] text-[#374151] mb-4">
            🎉 <span className="font-medium">Upcoming :</span> {upcoming.name} —{" "}
            {new Date(upcoming.date).toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
            })}
          </div>
        )}

        {/* TABLE */}
        <div className="border border-[#d1d5db] rounded-xl overflow-hidden relative">
          {/* Sized to the space left under the header, so the table scrolls
              inside the card instead of pushing the page into a scroll. */}
          <div
            ref={tableScrollRef}
            className="overflow-y-auto relative
  [&::-webkit-scrollbar]:hidden
  [-ms-overflow-style:none]
  [scrollbar-width:none]"
            style={{ maxHeight: "max(220px, calc(100vh - 340px))" }}
          >
            <table className="w-full text-[13px] border-collapse">
              <thead className="bg-[#f3f4f6] sticky top-0 z-10">
                <tr>
                  <th className="text-left px-6 py-3 border-b border-r">
                    Date
                  </th>
                  <th className="text-left px-4 py-3 border-b border-r">Day</th>
                  <th className="text-left px-4 py-3 border-b border-r">
                    Holiday Name
                  </th>
                  <th className="text-left px-4 py-3 border-b border-r">
                    Type
                  </th>
                  {canManageHolidays && (
                    <th className="text-left px-4 py-3 border-b">Action</th>
                  )}
                </tr>
              </thead>

              <tbody>
                {holidays.map((h) => {
                  const d = new Date(h.date);
                  const isPast = d < today;
                  const isEditing = editingHoliday?._id === h._id;

                  return (
                    <tr
                      key={h._id}
                      ref={isEditing ? editingRowRef : null}
                      className={`hover:bg-[#f9fafb] ${
                        isPast ? "opacity-50" : ""
                      }`}
                    >
                      {/* DATE */}
                      <td className="px-6 py-3 border-b border-r">
                        {isEditing ? (
                          <input
                            type="date"
                            value={editingHoliday.date?.split("T")[0] || ""}
                            onChange={(e) =>
                              setEditingHoliday({
                                ...editingHoliday,
                                date: e.target.value,
                              })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit();
                              if (e.key === "Escape") setEditingHoliday(null);
                            }}
                            className="border px-2 py-1 rounded w-full"
                          />
                        ) : (
                          d.toLocaleDateString("en-US", {
                            month: "short",
                            day: "2-digit",
                            year: "numeric",
                          })
                        )}
                      </td>

                      {/* DAY */}
                      <td className="px-4 py-3 border-b border-r text-[#6b7280]">
                        {d.toLocaleDateString("en-US", { weekday: "long" })}
                      </td>

                      {/* NAME */}
                      <td className="px-4 py-3 border-b border-r">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editingHoliday.name}
                            onChange={(e) =>
                              setEditingHoliday({
                                ...editingHoliday,
                                name: e.target.value,
                              })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit();
                              if (e.key === "Escape") setEditingHoliday(null);
                            }}
                            className="border px-2 py-1 rounded w-full"
                          />
                        ) : (
                          h.name
                        )}
                      </td>

                      {/* TYPE */}
                      <td className="px-4 py-3 border-b border-r">
                        {isEditing ? (
                          <select
                            value={editingHoliday.type}
                            onChange={(e) => {
                              const updated = {
                                ...editingHoliday,
                                type: e.target.value,
                              };
                              setEditingHoliday(updated);
                              handleSaveEdit(updated); // save immediately on selection
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Escape") setEditingHoliday(null);
                            }}
                            className="border px-2 py-1 rounded w-full"
                          >
                            <option>Public</option>
                            <option>Optional</option>
                            <option>Emergency</option>
                          </select>
                        ) : (
                          <span
                            className={`px-3 py-1 text-xs rounded-full ${
                              h.type === "Optional"
                                ? "bg-[#fef3c7] text-[#d97706]"
                                : h.type === "Emergency"
                                  ? "bg-[#fee2e2] text-[#dc2626]"
                                  : "bg-[#e0ecff] text-[#2563eb]"
                            }`}
                          >
                            {h.type}
                          </span>
                        )}
                      </td>

                      {/* DELETE */}
                      {canManageHolidays && (
                        <td className="px-4 py-3 border-b text-center">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingHoliday({
                                  ...h,
                                  date: h.date?.split("T")[0],
                                });
                              }}
                              className="inline-flex h-[18px] w-[18px] items-center justify-center hover:opacity-70"
                              aria-label={`Edit ${h.name}`}
                              title="Edit holiday"
                            >
                              <Icons.BPencil className="h-[18px] w-[18px]" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(h._id);
                              }}
                              className="inline-flex h-[18px] w-[18px] items-center justify-center hover:opacity-70"
                              aria-label={`Delete ${h.name}`}
                              title="Delete holiday"
                            >
                              <Trash2 size={18} stroke="red" strokeWidth={1.8} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <HoverScrollbar
            targetRef={tableScrollRef}
            watch={holidays}
            orientation="vertical"
          />
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-[598px] rounded-xl px-10 py-8 shadow-md font-['Poppins']">
            {/* HEADER */}
            <div className="flex justify-between items-center">
              <h2 className="text-[28px] leading-[42px] font-semibold text-black">
                Add Holiday
              </h2>

              <button
                onClick={() => setShowModal(false)}
                aria-label="Close add holiday dialog"
                className="text-black/50 hover:text-black"
              >
                ✕
              </button>
            </div>

            <p className="mt-2 text-[16px] leading-6 text-black/50">
              Create a new company holiday
            </p>

            <div className="border-b border-black/10 mt-4 mb-8"></div>

            {/* FORM */}
            <div className="space-y-6">
              {/* HOLIDAY NAME */}
              <div>
                <label className="block text-[16px] leading-6 text-black mb-2">Holiday Name</label>
                <input
                  type="text"
                  placeholder="Enter holiday name"
                  className="w-full h-10 border border-black/20 rounded-lg px-3 text-[14px] placeholder:text-black/30 outline-none focus:border-[#4253C7]"
                  value={newHoliday.name}
                  onChange={(e) =>
                    setNewHoliday({ ...newHoliday, name: e.target.value })
                  }
                />
              </div>

              {/* DATE */}
              <div>
                <label className="block text-[16px] leading-6 text-black mb-2">Date</label>

                <div className="flex relative h-10 border border-black/20 rounded-lg overflow-hidden focus-within:border-[#4253C7]">
                  {/* ICON (ONLY CLICK OPENS CALENDAR) */}
                  <div
                    className="w-[67px] flex items-center justify-center bg-black/[0.04] cursor-pointer text-[#4379FE]"
                    onClick={() =>
                      document.getElementById("hiddenDate").showPicker()
                    }
                  >
                    <Icons.Calendar />
                  </div>

                  {/* TEXT INPUT (MANUAL ENTRY) */}
                  <input
                    type="text"
                    placeholder="Enter your Date"
                    value={newHoliday.date}
                    onChange={(e) =>
                      setNewHoliday({ ...newHoliday, date: e.target.value })
                    }
                  className="w-full px-3 text-[14px] placeholder:text-black/30 bg-white outline-none"
                  />

                  {/* HIDDEN DATE PICKER */}
                  <input
                    id="hiddenDate"
                    type="date"
                    className="absolute opacity-0 pointer-events-none"
                    onChange={(e) =>
                      setNewHoliday({ ...newHoliday, date: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* TYPE */}
              <div>
                <label className="block text-[16px] leading-6 text-black mb-2">Type</label>

                <select
                  className="w-full h-10 border border-black/20 rounded-lg px-3 text-[14px]"
                  value={newHoliday.type}
                  onChange={(e) =>
                    setNewHoliday({ ...newHoliday, type: e.target.value })
                  }
                >
                  <option>Public</option>
                  <option>Optional</option>
                  <option>Emergency</option>
                </select>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex justify-end gap-3 mt-9">
              <button
                onClick={() => setShowModal(false)}
                className="h-10 border border-[#4253C7] text-[#4253C7] rounded-lg text-[14px] w-[163px]"
              >
                Cancel
              </button>

              <button
                onClick={handleAddHoliday}
                className="h-10 bg-[#4253C7] text-white rounded-lg text-[14px] w-[163px]"
              >
                Add Holiday
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white w-[400px] rounded-xl p-6 shadow-md">
            <h2 className="text-[18px] font-semibold text-gray-800 mb-2">
              Delete Holiday
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete this holiday? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-5 py-2 border border-gray-300 text-gray-600 rounded-md text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-5 py-2 bg-red-500 text-white rounded-md text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
